import React, { useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, Search, HelpCircle, Bell, Moon, Sun, Menu, X } from "lucide-react";
import { DarkModeContext } from "../../context/darkModeContext";
import { getAdminDetails } from "../../redux/actions/campaignActions";
import breadcrumbsFor from "../layout/breadcrumbMap";
import { useRole } from "../../hooks/useRole";

const NavbarV3 = ({ onMenuClick, isNavOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode, dispatch: themeDispatch } = useContext(DarkModeContext);
  const { hasRole, canDecide } = useRole();

  const adminView = useSelector((state) => state.adminView);
  const { adminDetails } = adminView || {};

  // WP6: getAdminDetails is an AD-ONLY endpoint, and this fired for every
  // session. A BDC, DR or Consultant logging in got an authorization failure
  // from a request they never asked for, surfaced as a generic error dialog on
  // top of a page that had otherwise loaded correctly — so the first thing every
  // non-AD user saw after signing in was an error about nothing they had done.
  //
  // Gated here rather than by relaxing the API: the endpoint returns the admin
  // record and is AD-only on purpose. The header does not need it either way —
  // `cachedAdmin` below already supplies the name from the login payload, which
  // is why this fetch failing was invisible apart from the dialog.
  const isAD = hasRole("AD");

  useEffect(() => {
    if (!isAD) return;
    const token = localStorage.getItem("accessToken");
    const id = JSON.parse(localStorage.getItem("admin") || "null")?.data?.id;
    if (token && id) {
      dispatch(getAdminDetails(token, id));
    }
  }, [dispatch, isAD]);

  // Which workspace's crumbs to use, not which authority the session has.
  // AD leads because a platform manager who also holds a compliance role still
  // lands on the ops desk; canDecide then covers DR, CCO and UDP alike; and
  // CONSULTANT sessions use the BDC pages, so they resolve to the BDC crumbs.
  const role = hasRole("AD")
    ? "AD"
    : canDecide
    ? "DR"
    : hasRole("BDC", "CONSULTANT")
    ? "BDC"
    : null;
  const crumbs = breadcrumbsFor(location.pathname, role);
  const leaf = crumbs[crumbs.length - 1];
  const stem = crumbs.slice(0, -1);

  const cachedAdmin = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("admin") || "null"); } catch (_) { return null; }
  }, []);
  const firstname =
    adminDetails?.data?.firstname ||
    cachedAdmin?.data?.firstname ||
    adminDetails?.data?.email?.split("@")[0] ||
    cachedAdmin?.data?.email?.split("@")[0] ||
    "";
  const initials = firstname ? firstname.slice(0, 2).toUpperCase() : "··";

  return (
    <header className="topbar">
      {onMenuClick && (
        <button
          className="topbar__menu"
          aria-label={isNavOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={isNavOpen ? "true" : "false"}
          aria-controls="app-sidebar"
          type="button"
          onClick={onMenuClick}
        >
          {isNavOpen ? <X /> : <Menu />}
        </button>
      )}
      <nav className="crumb">
        {stem.map((label, i) => (
          <React.Fragment key={i}>
            <span>{label}</span>
            <ChevronRight />
          </React.Fragment>
        ))}
        <b>{leaf}</b>
      </nav>

      <div className="cmd">
        <Search />
        <input placeholder="Search (coming soon)" disabled />
      </div>

      <button className="tb-icon" aria-label="Help" type="button">
        <HelpCircle />
      </button>
      <button className="tb-icon" aria-label="Notifications" type="button">
        <Bell />
      </button>
      <button
        className="tb-icon"
        aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
        type="button"
        onClick={() => themeDispatch({ type: "TOGGLE" })}
      >
        {darkMode ? <Sun /> : <Moon />}
      </button>
      <button
        className="tb-ava"
        aria-label="Profile"
        type="button"
        onClick={() => navigate("/profile")}
        style={{ border: 0 }}
      >
        {initials}
      </button>
    </header>
  );
};

export default NavbarV3;
